// import { faCloudArrowUp, faLink, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { get } from 'radash';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { Button } from '@/components/core/Button';
import { Dialog, DialogBody, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
// import { Icon } from '@/components/core/Icon';
// import { Image } from '@/components/core/Image';
import { Loader } from '@/components/core/Loader';
import { MIME_TYPES } from '@/constants';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { showAssetFoldersModal } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
// import { getFilenameFromUrl } from '@/services/utils';
import { LinkedAsset } from '@/types/AssetFolders';
import { DataModelAttribute } from '@/types/DataModel';

import { useAssetFolderUploader } from '../hooks/useAssetFolderUploader';
import { usePreviewValidator } from '../hooks/usePreviewValidator';
import { usePublicAsset } from '../hooks/usePublicAsset';
import { ACCEPTED_IMAGE_TYPES } from './ImageComponent';
// import { NonDrawableURLPreview } from './NonDrawableURLPreview';
import { PreviewDialog } from './PreviewDialog';
import { ProvideLinkDialog } from './ProvideLinkDialog';

interface ImageSelectDialog {
    attribute: DataModelAttribute;
    initialLink?: string;
    mode: 'Image' | 'Document';
    onApply: (url: string) => void;
    onClose: () => void;
    readonly: boolean;
    title: string;
}

const ImageSelectDialog = ({ attribute, initialLink, mode, onApply, onClose, readonly, title }: ImageSelectDialog) => {

    const { toast } = useToast();
    const { t } = useTranslation();
    const [link, setLink] = useState<string | undefined>();
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [, setMimeType] = useState('');
    const [, setDrawable] = useState(false);
    const [isProvideLinkDialogOpen, setIsProvideLinkDialogOpen] = useState(false);
    const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);

    const { validatePreviewAsync } = usePreviewValidator();
    const assetFolderUploader = useAssetFolderUploader();
    const onDropAccepted = useCallback(async (acceptedFiles: File[]) => {

        setLoadingPreview(true);

        const uploadedAssetUrl = await assetFolderUploader.uploadFile({
            file: acceptedFiles[0],
        });

        try {
            const { mimeType, drawable } = await validatePreviewAsync(uploadedAssetUrl);
            setMimeType(mimeType);
            setDrawable(drawable);
            setLink(uploadedAssetUrl);
            setLoadingPreview(false);
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            setMimeType('');
            setDrawable(false);
            setLink(uploadedAssetUrl);
            setLoadingPreview(false);
        }

        //eslint-disable-next-line
    }, []);

    const onDropRejected = useCallback(() => {
        toast.error('UPLOAD.NO_IMAGE_ERROR');
    }, [toast]);

    const {
        getRootProps: getDropZoneProps,
        getInputProps: getDropZoneInputProps,
        isDragActive,
    } = useDropzone({
        onDropAccepted,
        onDropRejected,
        maxFiles: 1,
        noClick: true,
        accept: mode === 'Image' ? ACCEPTED_IMAGE_TYPES : undefined,
    });

    useEffect(() => {
        if (initialLink) {
            onProvideLink(initialLink);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onProvideLink = async (link: string) => {
        setLoadingPreview(true);
        try {
            const { mimeType, drawable } = await validatePreviewAsync(link);
            setMimeType(mimeType);
            setDrawable(drawable);
            setLink(link);
            setLoadingPreview(false);
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            setMimeType('');
            setDrawable(false);
            setLink(link);
            setLoadingPreview(false);
        }
    };

    /**
     * FIXME: Update when we have asset folders in the next-app
     *
     * Open up the asset folders modal through the AngularJS bridge and set
     * the `publicAssetUrl`/`privateAssetUrl` as the attribute value when selected.
     */
    const { convertToPublicAsset } = usePublicAsset();
    const openAssetFoldersModal = () => {

        let acceptedTypes: string;

        if (mode === 'Image') {
            acceptedTypes = [
                MIME_TYPES.IMAGE_BMP,
                MIME_TYPES.IMAGE_GIF,
                MIME_TYPES.IMAGE_JPEG,
                MIME_TYPES.IMAGE_PNG,
                MIME_TYPES.IMAGE_SVG,
                MIME_TYPES.IMAGE_WEBP,
            ].join(',');
        } else {
            // Join all mime types
            acceptedTypes = Object.keys(MIME_TYPES).reduce((result, mimeType) => result += `,${get(MIME_TYPES, mimeType)}`, '');
        }

        showAssetFoldersModal(
            async (linkedAsset: LinkedAsset, linkedAssetPath: string) => {

                let url: string;

                try {
                    setLoadingPreview(true);

                    if (attribute.params?.confidential) {
                        url = linkedAsset.privateAssetUrl;
                    } else if (linkedAsset.publicAssetUrl) {
                        url = linkedAsset.publicAssetUrl;
                    } else {
                        url = await convertToPublicAsset(linkedAssetPath);
                    }

                    const { mimeType, drawable } = await validatePreviewAsync(url);
                    setMimeType(mimeType);
                    setDrawable(drawable);
                    setLink(url);
                    setLoadingPreview(false);

                } catch(exception) {
                    logger.error('ImageComponent: Could not select an asset via asset folders', exception);
                    setDrawable(false);
                } finally {
                    setLoadingPreview(false);
                }
            },
            acceptedTypes
        );
    };

    return (
        <Dialog onOpenChange={onClose} open={true}>
            <DialogContent variant="small" useReactPortal={true} onInteractOutside={e => e.preventDefault()} onPointerDownOutside={e => e.preventDefault()}>
                <DialogHeader>
                    <DialogHeaderDefault>{title}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody
                    padding="none"
                    className={`tw-flex tw-flex-row tw-space-x-4 tw-justify-center tw-items-center
                                tw-mt-2 tw-mx-2 tw-p-6 tw-overflow-auto tw-h-80 tw-border-dashed tw-border-gray-200 tw-border-2
                                ${isDragActive ? 'tw-bg-gray-200' : ''}`}
                    {...getDropZoneProps()}
                >

                    <input className="tw-hidden" {...getDropZoneInputProps()} />

                    {loadingPreview &&
                        <div className="tw-flex tw-flex-col tw-items-center">
                            <div className="tw-flex tw-items-center tw-justify-center tw-h-[200px] tw-w-[200px] tw-rounded-md tw-bg-gray-100">
                                <Loader />
                            </div>
                        </div>
                    }

                    {/* {link &&
                        <div className="tw-flex tw-flex-col tw-items-center">
                            <div
                                className="tw-flex tw-items-center tw-justify-center tw-h-[200px] tw-w-[200px] tw-rounded-md tw-bg-gray-100 tw-cursor-pointer"
                                onClick={() => setIsPreviewDialogOpen(true)}>
                                {
                                    drawable ?
                                        <Image className="tw-w-full tw-h-full tw-object-contain" title={title} src={link} />
                                        :
                                        <NonDrawableURLPreview mimeType={mimeType} />
                                }
                            </div>
                            <a href={link} target="_blank" className="tw-text-xs" rel="noreferrer">{getFilenameFromUrl(link)}</a>
                        </div>
                    } */}

                    {isPreviewDialogOpen && link && (
                        <PreviewDialog
                            url={link}
                            onClose={() =>
                                setIsPreviewDialogOpen(false)
                            }
                        />
                    )}

                    {/* Selection and link providing section, only allowed on non-readonly attributes */}
                    {
                        !readonly &&
                        <div className="tw-flex tw-flex-col tw-space-y-4">
                            <Button variant="secondary" className="tw-mx-auto" onClick={() => setIsProvideLinkDialogOpen(true)}>
                                {/* <Icon icon={faLink} size="sm" variant="secondary" /> */}
                                <span className="tw-mx-1">{t('UPLOAD.PROVIDE_LINK')}</span>
                            </Button>
                            {isProvideLinkDialogOpen && <ProvideLinkDialog onApply={onProvideLink} onClose={() => setIsProvideLinkDialogOpen(false)} />}

                            <div className="tw-flex tw-items-center tw-justify-center">
                                {/* <Icon icon={faCloudArrowUp} size="sm" variant="dark" colorScheme="darker" /> */}
                                <span className="tw-mx-1 tw-text-t-sm">{t('UPLOAD.DRAG_AND_DROP')}</span>
                            </div>

                            <Button variant="secondary" className="tw-mx-auto" onClick={openAssetFoldersModal}>
                                {/* <Icon icon={faMagnifyingGlass} size="sm" variant="secondary" /> */}
                                <span className="tw-mx-1">{t('UPLOAD.BROWSE')}</span>
                            </Button>
                        </div>
                    }

                </DialogBody>
                <div className="tw-my-2 tw-text-center tw-text-t-sm">
                    {t('UPLOAD.SUPPORTED_IMAGE_FORMATS')}
                </div>
                <DialogFooter>
                    <Button variant="default" type="button" disabled={!link} onClick={() => link && onApply(link)}>{t('APPLY_CHANGES')}</Button>
                    <DialogClose asChild={true}>
                        <Button variant="secondary" type="submit">{t('CANCEL')}</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { ImageSelectDialog };
